import React, { createContext, useContext, useState } from 'react';
import locales from 'locales/locales';

const langOptions = [
    { code: 'en', label: 'English', flag: '/img/lang_en.svg' },
    //{ code: 'uk', label: 'Українська', flag: '/img/lang_uk.svg' },
    { code: 'ru', label: 'Русский', flag: '/img/lang_ru.svg' },
    { code: 'he', label: 'עברית', flag: '/img/lang_he.svg' },
];
export function getLangOptions(): typeof langOptions {
    const browserLang = navigator.language || navigator.languages[0] || 'en';
    const shortLang = browserLang.split('-')[0];
    
    const systemLangIndex = langOptions.findIndex(opt => opt.code === shortLang);
    if (systemLangIndex === -1) return langOptions;

    const reordered = [langOptions[systemLangIndex], ...langOptions.filter((_, i) => i !== systemLangIndex)];
    return reordered;
}

function getDefaultLanguage(): string {
    const savedLang = localStorage.getItem("lang");
    if (savedLang) {
        return savedLang;
    }
    const browserLang = navigator.language || navigator.languages[0] || 'en';
    const shortLang = browserLang.split('-')[0];
    const supported = langOptions.map(opt => opt.code);
    return supported.includes(shortLang) ? shortLang : 'en';
}

export interface LanguageContextType {
    language: string;
    setLanguage: (lang: string) => void;
    translate: (key: string) => string;
    fetchLocalized: (path: string, options?: RequestInit) => Promise<Response>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState(getDefaultLanguage());

    const setLanguage = (lang: string) => {
        localStorage.setItem('lang', lang);
        setLanguageState(lang);
        console.log("Lang", lang);
    };

    const translate = (key: string): string => {
        //return locales[language]?.[key] || key;
        return (locales as Record<string, Record<string, string>>)[language]?.[key] || key;
    };

    const localizePath = (path: string): string => {
        const defaultLang = 'en';
        const currentLang = language || defaultLang;
        if (currentLang === defaultLang) {
            return path;
        }
    
        const suffix = `_${currentLang}`;
        const i = path.lastIndexOf('.');
        return i === -1 ? path + suffix : path.slice(0, i) + suffix + path.slice(i);
    }

     const fetchLocalized = async (path: string, options?: RequestInit): Promise<Response> => {
        const localizedPath = localizePath(path);
        try {
            const response = await fetch(localizedPath, options);
            if (!response.ok) throw new Error("Localized fetch failed");
            return response;
        } catch {
            // fallback to default
            return await fetch(path, options);
        }
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, translate, fetchLocalized }}>
            {children}
        </LanguageContext.Provider>
    );
};

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
}
